import { consume } from "@lit/context";
import { html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { authContext, type AuthState } from "../provider/kc-context.js";

/**
 * A link component that navigates to the Keycloak Account Console when clicked.
 *
 * This component only renders when the user IS authenticated. It wraps your custom
 * link or button element and navigates to the Keycloak Account Console where users
 * can manage their profile, password, sessions, and other account settings.
 *
 * @element kc-account-link
 *
 * @attr {string} [redirect-uri] - URI to return to after leaving the Account Console
 *
 * @slot - Default slot for custom link content (e.g., `<a>My Account</a>`)
 *
 * @example Basic usage
 * ```html
 * <kc-account-link>
 *   <a href="#">My Account</a>
 * </kc-account-link>
 * ```
 *
 * @example With custom redirect
 * ```html
 * <kc-account-link redirect-uri="https://myapp.com/profile">
 *   <a href="#">Manage Profile</a>
 * </kc-account-link>
 * ```
 *
 * @example As a button
 * ```html
 * <kc-account-link>
 *   <button class="btn-link">
 *     <i class="icon-user"></i> Account Settings
 *   </button>
 * </kc-account-link>
 * ```
 *
 * @example In navigation menu
 * ```html
 * <nav>
 *   <kc-account-link>
 *     <a class="nav-link">Profile</a>
 *   </kc-account-link>
 *   <kc-logout-button>
 *     <a class="nav-link">Logout</a>
 *   </kc-logout-button>
 * </nav>
 * ```
 *
 * @remarks
 * The Account Console URL is generated by Keycloak and may not be available
 * for all OIDC providers. If the URL cannot be generated, the click will be ignored.
 */
@customElement("kc-account-link")
export class KcAccountLink extends LitElement {
  @consume({ context: authContext, subscribe: true })
  @state()
  private auth: AuthState = {};

  /**
   * URI to return to after leaving the Account Console.
   * This is passed to Keycloak as the `referrer_uri` parameter.
   * If not specified, Keycloak will use its default redirect behavior.
   *
   * @example "https://myapp.com/dashboard"
   */
  @property({ type: String, attribute: "redirect-uri" })
  redirectUri?: string;

  /**
   * Handles account link click by navigating to the Keycloak Account Console.
   * Uses window.location.assign to perform a full page navigation.
   *
   * @private
   */
  private onAccountClick = () => {
    const kc = this.auth.keycloak;
    if (kc == null) {
      return;
    }
    try {
      const opts =
        this.redirectUri != null && this.redirectUri !== ""
          ? { redirectUri: this.redirectUri }
          : undefined;
      window.location.assign(kc.createAccountUrl(opts));
    } catch {
      // Generic OIDC adapters may not support account URLs.
      // Silently ignore if createAccountUrl is not available.
    }
  };

  /**
   * Renders the slot with account link click handler only when user is authenticated.
   * Returns empty template when not authenticated or Keycloak is not initialized.
   */
  render() {
    if (this.auth.keycloak != null && this.auth.authenticated === true) {
      return html`<slot @click=${this.onAccountClick}></slot>`;
    }
    return html``;
  }
}
